import { Injectable, inject } from '@angular/core';
import {
  Auth,
  User,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  user,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';

const STAFF_ROLES: ReadonlySet<unknown> = new Set(['admin', 'operator', 'superadmin']);

const LOGIN_ERRORS: Partial<Record<string, string>> = {
  'auth/invalid-credential': 'Wrong email or password.',
  'auth/invalid-email': 'Enter a valid email address.',
  'auth/user-disabled': 'This account is disabled.',
  'auth/too-many-requests': 'Too many attempts. Try again later.',
  'auth/network-request-failed': 'No connection. Check your internet and try again.',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly #auth = inject(Auth);
  readonly #router = inject(Router);

  /** Live staff check (Main leaves when it turns false); re-checked on sign-in, sign-out and every token refresh. */
  readonly isStaff$ = user(this.#auth).pipe(switchMap(async (currentUser) => !!currentUser && (await hasStaffRole(currentUser))));

  /** One-shot check for guards: waits for Firebase to restore the session, then reads the current user (no stream timing). */
  async isStaff(): Promise<boolean> {
    await this.#auth.authStateReady();
    const currentUser = this.#auth.currentUser;
    return !!currentUser && (await hasStaffRole(currentUser));
  }

  /** Signs in staff only; anyone else is signed out again and gets an error. */
  async login(email: string, password: string, stayLoggedIn: boolean): Promise<void> {
    // Local: survives closing the browser. Session: ends when the tab is closed (shared computers).
    await setPersistence(this.#auth, stayLoggedIn ? browserLocalPersistence : browserSessionPersistence);
    const credential = await signInWithEmailAndPassword(this.#auth, email, password).catch((error: { code?: string }) => {
      throw new Error((error.code && LOGIN_ERRORS[error.code]) || 'Login failed. Please try again.');
    });
    if (!(await hasStaffRole(credential.user))) {
      await this.signOut();
      throw new Error('This app is only for admins and operators.');
    }
  }

  /** Ends the session without navigating: Main redirects to Login, guards return their own redirect. */
  signOut(): Promise<void> {
    return signOut(this.#auth);
  }

  /** Used only by Main when the session ends, so there is exactly one navigation to Login. */
  async logout(): Promise<void> {
    await this.signOut();
    await this.#router.navigate(['/login']);
  }
}

/** Role claim on the ID token (set by sync-claims.js); reads the cached token, no network call. */
async function hasStaffRole(user: User): Promise<boolean> {
  const { claims } = await user.getIdTokenResult();
  return STAFF_ROLES.has(claims['role']);
}
